import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, PlaySquareTwoTone, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useContentCards } from 'src/features/contents/contents.hooks';
import { fetchContents } from 'src/features/contents/contents.state';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import tileStyles from 'src/styles/tile.module.css';
import { Link, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import {
  addCardsToCollection,
  removeCardsFromCollection,
  updateCollection,
} from 'src/features/collections/collections.state';
import { useCollectionById, useCollectionCardsById } from 'src/features/collections/collections.hooks';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import { ContentCard } from 'src/features/contents/contents.types';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { CSS } from '@dnd-kit/utilities';
import { Collection } from 'src/features/collections/collections.types';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';

interface CollectionAddVideosProps {
  collectionId: string;
  currentCards: ContentCard[];
}

const CollectionAddVideos: React.FC<CollectionAddVideosProps> = props => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const cards = useContentCards();
  const [name, setName] = useState<string | undefined>(undefined);
  const currentCardIds = useMemo(() => props.currentCards.map(c => c.id), [props.currentCards]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddCardButtonClick = useCallback(
    async (cardId: string) => {
      setIsLoading(true);
      await dispatch(addCardsToCollection({ collectionId: props.collectionId, data: { ids: [cardId] } }));
      setIsLoading(false);
    },
    [dispatch, props.collectionId]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(fetchContents({}));
      setIsLoading(false);
    })();
  }, [dispatch]);
  useDebounce(
    () => {
      if (name !== undefined && !isLoading) {
        (async () => {
          setIsLoading(true);
          await dispatch(fetchContents({ params: { name: name } }));
          setIsLoading(false);
        })();
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <>
      <Input
        autoFocus
        allowClear
        placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
        prefix={<SearchOutlined />}
        onChange={e => {
          setName(e.target.value);
        }}
        style={{ marginBottom: 10 }}
      />
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          {cards
            .filter(c => !currentCardIds.includes(c.id) && !c.archived)
            .map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/content/cards/${c.id}/collections`} className={tileStyles.tileName}>
                  <Space>
                    <PlaySquareTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {c.archived && (
                    <Tag color={'orange'}>
                      <FormattedMessage id={'text.archived'} />
                    </Tag>
                  )}
                  <Button type={'primary'} size='small' onClick={() => handleAddCardButtonClick(c.id)}>
                    <FormattedMessage id={'action.Add'} />
                  </Button>
                </Space>
              </div>
            ))}
        </LoaderWrapper>
      </div>
    </>
  );
};

const SortableItem: React.FC<PropsWithChildren<{ id: string; key: string }>> = props => {
  const { children } = props;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  } as React.CSSProperties;
  return (
    <div style={style} ref={setNodeRef} {...attributes} {...listeners} className={tileStyles.listTile}>
      {children}
    </div>
  );
};

const CollectionContentCardsTab: React.FC = () => {
  const { collectionId } = useParams() as { collectionId: string };
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const collection = useCollectionById(collectionId) as Collection;
  const cards = useCollectionCardsById(collectionId);
  const dispatch: AppDispatch = useDispatch();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [isLoading, setIsLoading] = useState(false);
  const handleRemoveCardButtonClick = useCallback(
    async (cardId: string) => {
      setIsLoading(true);
      await dispatch(removeCardsFromCollection({ collectionId: collectionId, data: { ids: [cardId] } }));
      setIsLoading(false);
    },
    [dispatch, collectionId]
  );

  const handleAddButtonClick = useCallback(() => {
    setAddMode(m => !m);
  }, []);
  const handleEditButtonClick = useCallback(() => {
    setEditMode(m => !m);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = cards.findIndex((i: ContentCard) => i.id === active.id);
        const newIndex = cards.findIndex((i: ContentCard) => i.id === over?.id);
        const result = arrayMove(cards, oldIndex, newIndex);
        setIsLoading(true);
        await dispatch(
          updateCollection({
            collectionId: collection.id,
            data: {
              cardsOrder: result.map(r => r.id),
            },
          })
        );
        setIsLoading(false);
      }
    },
    [dispatch, collection, cards]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Видео</Typography.Title>
        <Space direction={'horizontal'}>
          {!editMode && (
            <Button type={addMode ? undefined : 'primary'} onClick={handleAddButtonClick}>
              {addMode ? <CloseOutlined /> : <PlusOutlined />}
            </Button>
          )}
          {!addMode && (
            <Button onClick={handleEditButtonClick}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        {addMode && <CollectionAddVideos collectionId={collectionId} currentCards={cards} />}
        {!addMode && (
          <LoaderWrapper loading={isLoading}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={cards || []} strategy={verticalListSortingStrategy}>
                {cards?.map(
                  c =>
                    c && (
                      <SortableItem key={c.id} id={c.id}>
                        <Link to={`/content/cards/${c.id}/collections`} className={tileStyles.tileName}>
                          <Space>
                            <PlaySquareTwoTone />
                            {c.name}
                          </Space>
                        </Link>
                        <Space>
                          <Space>
                            {c.disabled && <Tag>Заблокирована</Tag>}
                            {c.archived && <Tag color={'warning'}>Архивная</Tag>}
                            <Space size={0}>{c.devices && c.devices.map(d => <Tag key={d}>{d}</Tag>)}</Space>
                          </Space>
                          {editMode && (
                            <ConfirmButton danger size='small' onClick={() => handleRemoveCardButtonClick(c.id)}>
                              <FormattedMessage id={'action.Remove'} />
                            </ConfirmButton>
                          )}
                        </Space>
                      </SortableItem>
                    )
                )}
              </SortableContext>
            </DndContext>
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};
export default CollectionContentCardsTab;
